import { FC, useEffect, useState } from 'react'
import { positions, useAlert } from 'react-alert'
import CopyToClipboard from 'react-copy-to-clipboard'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { useAppDispatch, useAppSelector } from '@/core/store/hooks'
import { cn } from '@bem-react/classname'

import { useHandleServerError } from '@/hooks/useHandleServerError'

import { Back } from '@/components/UI/Back'
import { Button } from '@/components/UI/Button'
import { Loading } from '@/components/UI/Loading'
import { Modal } from '@/components/UI/Modal'
import { Timer } from '@/components/UI/Timer'
import { Title } from '@/components/UI/Title'

import './Order.scss'
import { orderSelector } from './store/OrdersSelector'
import { closeOrderThunk, getOrderThunk } from './store/OrdersSlice'

const cnOrder = cn('Order')

export const Order: FC = () => {
  const alert = useAlert()
  const { id } = useParams<{ id: string }>()
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { handleServerError } = useHandleServerError()

  const order = useAppSelector(orderSelector)

  const [isLoading, setLoading] = useState<boolean>(true)
  const [isOpenModal, setOpenModal] = useState<boolean>(false)
  const [isVisibleTimer, setVisibleTimer] = useState<boolean>(false)

  useEffect(() => {
    if (id) getOrder(id)
  }, [id])

  useEffect(() => {
    if (order && order.awaiting_payment) setVisibleTimer(true)
  }, [order])

  const getOrder = async (id: string) => {
    try {
      await dispatch(getOrderThunk(id))
      setLoading(false)
    } catch (error) {
      handleServerError(error)
      setLoading(false)
    }
  }

  const onClick = () => {
    if (!order) return

    alert.show(
      `${t('receive-alert-title')}|${t('receive-alert-text', { address: order.wallet })}`,
      {
        type: 'success',
        position: positions.BOTTOM_RIGHT,
      },
    )
  }

  const onClickChangeRoute = () => {
    // navigate(`/c/orders/${id}/payment`)
    if (order?.pay) {
      window.open(order.pay.pay_url, '_self')
      // window.open(order.pay.pay_url, 'blank')
    }
  }

  const closeOrder = async (id: string) => {
    try {
      await dispatch(closeOrderThunk(id))
    } catch (error) {
      handleServerError(error)
    }
  }

  return (
    <>
      {isLoading ? (
        <Loading type='circle' minHeight='300px' />
      ) : (
        <>
          {order && (
            <div className={cnOrder()}>
              <div className={cnOrder('top')}>
                <Back />
                <Title>
                  {t('order-title')} #{order.id}
                </Title>

                {isVisibleTimer && (
                  <Timer
                    end={order.cancel_timestamp}
                    className={cnOrder('timer')}
                    onFinish={() => setVisibleTimer(false)}
                  />
                )}
              </div>

              <div className={cnOrder('grid')}>
                <div className={cnOrder('item')}>
                  <div className={cnOrder('value')}> {order.id}</div>
                  <div className={cnOrder('label')}>{t('order-label-1')}</div>
                </div>
                <div className={cnOrder('item')}>
                  <div className={cnOrder('value')}>
                    {order.date} {order.time}
                  </div>
                  <div className={cnOrder('label')}>{t('order-label-2')}</div>
                </div>
                <div className={cnOrder('item')}>
                  <div className={cnOrder('value')}>
                    {t(`orders-method-${order.payment_method.toLowerCase()}`)}
                  </div>
                  <div className={cnOrder('label')}>{t('order-label-3')}</div>
                </div>
                <div className={cnOrder('item')}>
                  <div
                    className={cnOrder('value', {
                      new: order.status.name.toLowerCase() === 'new',
                      canceled: order.status.name.toLowerCase() === 'cancel',
                      completed:
                        order.status.name.toLowerCase() === 'completed',
                      process: order.status.name.toLowerCase() === 'process',
                    })}
                  >
                    {t(`orders-status-${order.status.name.toLowerCase()}`)}
                  </div>
                  <div className={cnOrder('label')}>{t('order-label-4')}</div>
                </div>
                <div className={cnOrder('item')}>
                  <div className={cnOrder('value')}>
                    {parseFloat(order.sum_pay)} {order.from}
                  </div>
                  <div className={cnOrder('label')}>{t('order-label-5')}</div>
                </div>
                <div className={cnOrder('item')}>
                  <div className={cnOrder('value')}>
                    {parseFloat(order.sum_buy)} {order.to}
                  </div>
                  <div className={cnOrder('label')}>{t('order-label-6')}</div>
                </div>
                <div className={cnOrder('item')}>
                  <div className={cnOrder('value')}>
                    {parseFloat(order.fee_all)} {order.from}
                  </div>
                  <div className={cnOrder('label')}>{t('order-label-7')}</div>
                </div>

                {order.wallet && (
                  <div className={cnOrder('item')}>
                    <CopyToClipboard text={order.wallet} onCopy={onClick}>
                      <div className={cnOrder('value', { copy: true })}>
                        {order.wallet}
                      </div>
                    </CopyToClipboard>

                    <div className={cnOrder('label')}>
                      {t('order-label-8', { code: order.to })}
                    </div>
                  </div>
                )}

                {order.tx && order.tx_url && (
                  <div className={cnOrder('item')}>
                    <div className={cnOrder('value')}>
                      <a
                        href={order.tx_url}
                        target='_blank'
                        className={cnOrder('link')}
                      >
                        {order.tx}
                      </a>
                    </div>
                    <div className={cnOrder('label')}>{t('order-label-9')}</div>
                  </div>
                )}
              </div>
              {isVisibleTimer && (
                <div className={cnOrder('actions')}>
                  <Button onClick={() => setOpenModal(true)}>Confirm</Button>
                </div>
              )}

              {order.status.name.toLowerCase() === 'new' && (
                <div className={cnOrder('actions')}>
                  <Button onClick={() => closeOrder(order.invoice_id)}>
                    Close order
                  </Button>
                </div>
              )}
            </div>
          )}

          <Modal
            open={isOpenModal}
            onClose={() => setOpenModal(false)}
            styles={{ maxWidth: 350 }}
            className={cnOrder('modal')}
          >
            <div className={cnOrder('icon')}>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='24'
                height='24'
                viewBox='0 0 24 24'
                fill='none'
              >
                <path
                  d='M12 8H12.01M12 11V16M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z'
                  stroke='#30DAAD'
                  stroke-width='1.5'
                  stroke-linecap='round'
                  stroke-linejoin='round'
                />
              </svg>
            </div>
            <div className={cnOrder('text')}>
              The purchase rate may change slightly during the payment process.
              If the rate changes, you may receive either more or less. Are you
              willing to proceed? Please click Yes or No.
            </div>

            <div className={cnOrder('btns')}>
              <Button onClick={onClickChangeRoute}>Yes</Button>
              <Button color='light' onClick={() => setOpenModal(false)}>
                No
              </Button>
            </div>
          </Modal>
        </>
      )}
    </>
  )
}
